/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react';
import Model, { Providers } from '@tripian/model';
import classes from './KiwiHotelCard.scss';
import KiwiHotelCardImage from './KiwiHotelCardImage/KiwiHotelCardImage';
import KiwiVisaBenefitsModal from './KiwiVisaBenefitsModal/KiwiVisaBenefitsModal';
import { getCurrencySymbol } from '../currencySymbol';

interface IKiwiHotelCard {
  hotel: Providers.Kiwi.KiwiSearchHotel;
  isFavorite?: boolean;
  onFavoriteToggle?: (hotel: Providers.Kiwi.KiwiSearchHotel) => void;
  showVisaBadge?: boolean;
  onHover?: (hotelCode: string | null) => void;
  onSpecialOffersClick?: (hotel: Providers.Kiwi.KiwiSearchHotel) => void;
  onCardClick?: (hotel: Providers.Kiwi.KiwiSearchHotel) => void;
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  children?: number;
  t: (value: Model.TranslationKey) => string;
}

const buildBookingUrl = (catalogUrl: string, checkIn?: string, checkOut?: string, adults?: number, children?: number): string => {
  const base = catalogUrl.replace(/\/$/, '');
  if (!checkIn || !checkOut) return base;
  return `${base}/${checkIn}/${checkOut}/${adults ?? 2}/${children ?? 0}/1`;
};

const KiwiHotelCard: React.FC<IKiwiHotelCard> = ({ hotel, isFavorite, onFavoriteToggle, showVisaBadge, onHover, onSpecialOffersClick, onCardClick, checkIn, checkOut, adults, children, t }) => {
  const [visaModalOpen, setVisaModalOpen] = useState(false);
  const starCount = Math.min(5, Math.round(parseFloat(hotel.starRating) || 0));
  const score = parseFloat(hotel.kiwiOverallScore) || 0;
  const minRate = hotel.minimumRate || hotel.lowestNightRate;
  const price = minRate ? parseFloat(minRate) : 0;

  /* Collect all image URLs */
  const imageUrls: string[] = [];
  if (hotel.mainImageUrl) imageUrls.push(hotel.mainImageUrl);
  if (hotel.images && hotel.images.length > 0) {
    hotel.images.forEach((img) => {
      if (img.url && img.url !== hotel.mainImageUrl) imageUrls.push(img.url);
    });
  }

  const locationText = [hotel.city, hotel.country].filter(Boolean).join(', ');
  const perks = (hotel.interests || []).slice(0, 3);
  const roomCount = hotel.numberOfRooms;
  const settings = (hotel.settings || []).slice(0, 2).join(', ');
  const styles = (hotel.styles || []).slice(0, 2).join(', ');
  const hasSpecialOffer = hotel.hasSpecialOffer === 'true';
  const hasInfoTable = perks.length > 0 || hasSpecialOffer || roomCount || settings || styles;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classes.card}
      onMouseEnter={() => onHover?.(hotel.code)}
      onMouseLeave={() => onHover?.(null)}
      onClick={() => onCardClick?.(hotel)}
      role={onCardClick ? 'button' : undefined}
      tabIndex={onCardClick ? 0 : undefined}
    >
      {/* ── Left: Image slider + favorite ── */}
      <div className={classes.imageArea}>
        <KiwiHotelCardImage images={imageUrls} alt={hotel.title} isFavorite={isFavorite} onFavoriteToggle={onFavoriteToggle ? () => onFavoriteToggle(hotel) : undefined} />
      </div>

      {/* ── Right: Content ── */}
      <div className={classes.content}>
        {/* Row 1 — score pill ←→ price + book */}
        <div className={classes.row1}>
          <div className={classes.scoreBadge}>
            <svg className={classes.scoreStar} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </svg>
            <span className={classes.scoreNum}>{score > 0 ? score.toFixed(1) : '—'}</span>
          </div>

          <div className={classes.priceBook}>
            {price > 0 && (
              <span className={classes.price}>
                {getCurrencySymbol(hotel.currencyCode)}{price.toFixed(0)}
                <span className={classes.priceUnit}>{t('kiwi.common.perNight')}</span>
              </span>
            )}
            <button
              type="button"
              className={classes.bookBtn}
              onClick={(e) => {
                e.stopPropagation();
                if (hotel.catalogUrl) window.open(buildBookingUrl(hotel.catalogUrl, checkIn, checkOut, adults, children), '_blank', 'noopener,noreferrer');
              }}
            >
              {t('kiwi.card.book')}
            </button>
          </div>
        </div>

        {/* Row 2 — name + gold stars */}
        <div className={classes.row2}>
          <span className={classes.name}>{hotel.title}</span>
          {starCount > 0 && (
            <span className={classes.goldStars}>
              {Array.from({ length: starCount }, (_, i) => (
                <svg key={i} className={classes.goldStar} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
              ))}
            </span>
          )}
        </div>

        {/* Row 3 — location */}
        {locationText && <span className={classes.location}>{locationText}</span>}

        {/* ── Info table ── */}
        {hasInfoTable && (
          <div className={classes.infoTable}>
            {/* Perks row */}
            {perks.length > 0 && (
              <div className={classes.perksRow}>
                <div className={classes.infoCell}>
                  <div className={classes.perksLabelRow}>
                    <span className={classes.cellLabel}>{t('kiwi.card.perks')}</span>
                    {showVisaBadge && (
                      // eslint-disable-next-line jsx-a11y/control-has-associated-label
                      <span
                        className={classes.visaBadge}
                        onClick={(e) => {
                          e.stopPropagation();
                          setVisaModalOpen(true);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={() => {}}
                        aria-label={t('kiwi.card.visaPremiumBenefits')}
                      />
                    )}
                  </div>
                </div>
                <div className={classes.infoCell}>
                  {hasSpecialOffer ? (
                    <div
                      className={classes.perksLabelRow}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onSpecialOffersClick) onSpecialOffersClick(hotel);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={() => {}}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className={classes.cellLabel}>{t('kiwi.common.specialOffers')}</span>
                      <svg className={classes.specialOfferIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                        <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
                      </svg>
                    </div>
                  ) : (
                    <span className={classes.cellValue}>{perks.join(' \u00B7 ')}</span>
                  )}
                </div>
              </div>
            )}

            {/* Details row — 3 columns with badge values */}
            {(roomCount || settings || styles) && (
              <div className={classes.infoRow}>
                {roomCount && (
                  <div className={`${classes.infoCell} ${classes.cellThird}`}>
                    <span className={classes.cellLabel}>{t('kiwi.common.roomsSuites')}</span>
                    <span className={classes.valueBadge}>{roomCount}</span>
                  </div>
                )}
                {settings && (
                  <div className={`${classes.infoCell} ${classes.cellThird}`}>
                    <span className={classes.cellLabel}>{t('kiwi.common.setting')}</span>
                    <span className={classes.valueBadge}>{settings}</span>
                  </div>
                )}
                {styles && (
                  <div className={`${classes.infoCell} ${classes.cellThird}`}>
                    <span className={classes.cellLabel}>{t('kiwi.common.style')}</span>
                    <span className={classes.valueBadge}>{styles}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {visaModalOpen && <KiwiVisaBenefitsModal onClose={() => setVisaModalOpen(false)} t={t} />}
    </div>
  );
};

export default KiwiHotelCard;
